import type { SeriesNullablePoints } from "@/components/charts/GRPCMetricChartImpl";
import { getGrpcStatusName } from "@/components/access-logs/AccessLogsUtils";
import { getRequiredTailwindColorHexByName } from "@/utils/color-util";

// Shared with the /logs/access-logs page so its charts and the dashboard tiles draw alike.

const ACCESS_LOGS_STATUS_COLORS = {
  blue500: getRequiredTailwindColorHexByName("blue-500"),
  emerald500: getRequiredTailwindColorHexByName("emerald-500"),
  gray500: getRequiredTailwindColorHexByName("gray-500"),
  orange500: getRequiredTailwindColorHexByName("orange-500"),
  red500: getRequiredTailwindColorHexByName("red-500"),
  yellow500: getRequiredTailwindColorHexByName("yellow-500"),
} as const;

// Keyed by the gRPC status *name*, so numeric codes need no second set of entries: they are named
// first. Anything unlisted (including an unrecognized name) falls through to gray.
const GRPC_STATUS_COLORS: Record<string, string> = {
  OK: ACCESS_LOGS_STATUS_COLORS.emerald500,
  CANCELLED: ACCESS_LOGS_STATUS_COLORS.gray500,
  UNKNOWN: ACCESS_LOGS_STATUS_COLORS.red500,
  INVALID_ARGUMENT: ACCESS_LOGS_STATUS_COLORS.yellow500,
  DEADLINE_EXCEEDED: ACCESS_LOGS_STATUS_COLORS.gray500,
  NOT_FOUND: ACCESS_LOGS_STATUS_COLORS.yellow500,
  ALREADY_EXISTS: ACCESS_LOGS_STATUS_COLORS.yellow500,
  PERMISSION_DENIED: ACCESS_LOGS_STATUS_COLORS.orange500,
  RESOURCE_EXHAUSTED: ACCESS_LOGS_STATUS_COLORS.red500,
  FAILED_PRECONDITION: ACCESS_LOGS_STATUS_COLORS.red500,
  ABORTED: ACCESS_LOGS_STATUS_COLORS.red500,
  OUT_OF_RANGE: ACCESS_LOGS_STATUS_COLORS.yellow500,
  UNIMPLEMENTED: ACCESS_LOGS_STATUS_COLORS.yellow500,
  INTERNAL: ACCESS_LOGS_STATUS_COLORS.red500,
  UNAVAILABLE: ACCESS_LOGS_STATUS_COLORS.red500,
  DATA_LOSS: ACCESS_LOGS_STATUS_COLORS.red500,
  UNAUTHENTICATED: ACCESS_LOGS_STATUS_COLORS.orange500,
};

const HTTP_CLASS_COLORS: Record<string, string> = {
  "2": ACCESS_LOGS_STATUS_COLORS.emerald500,
  "3": ACCESS_LOGS_STATUS_COLORS.blue500,
  "4": ACCESS_LOGS_STATUS_COLORS.yellow500,
  "5": ACCESS_LOGS_STATUS_COLORS.red500,
};

// Three digits is an HTTP status, coloured by class; anything else is a gRPC code, named first so a
// numeric code and its name resolve alike. Length is what separates them: HTTP 500 vs gRPC 5.
// Undefined for a label that is not a status at all, which is not the same as "grey": see below.
//
// "0" is neither HTTP nor gRPC: it is the status_code facet's no-response value, from Envoy's own
// `response_code: 0` and from rows whose ResponseCode never parsed. Without this it reads as gRPC 0
// and paints a failed request success-green.
export const accessLogStatusColor = (status: string): string | undefined => {
  if (status === "0") {
    return ACCESS_LOGS_STATUS_COLORS.gray500;
  }
  if (/^\d{3}$/.test(status)) {
    return HTTP_CLASS_COLORS[status[0]];
  }
  return GRPC_STATUS_COLORS[getGrpcStatusName(status)];
};

// Series-derived rather than a fixed record: the codes present vary per query.
//
// Only recognized statuses get an entry. A label this cannot read — a measure series like
// "p95(duration)", or a breakdown by something other than status — is left out so the chart's own
// palette colours it. Assigning grey instead painted every series of a multi-measure duration chart
// the same, which is exactly when telling them apart matters.
export const buildAccessLogColorOverrides = (
  series: SeriesNullablePoints[],
): Record<string, string> => {
  const colors: Record<string, string> = {};
  for (const s of series) {
    const color = s.label ? accessLogStatusColor(s.label) : undefined;
    if (color != null) colors[s.label] = color;
  }
  return colors;
};

export const sortAccessLogSeries = (series: SeriesNullablePoints[]): SeriesNullablePoints[] =>
  series.toSorted((a, b) => {
    const aNum = parseInt(a.label);
    const bNum = parseInt(b.label);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    return a.label.localeCompare(b.label);
  });
